import React from 'react';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import language from '../../utils/LanguageSettings';
import { normalizeSiteLang } from '../../utils/userIdentity';
import { SSP_URL } from '../../config';

interface RecommendationProps {

}

const resolveRuntimeLanguage = (searchParams: URLSearchParams): 'en' | 'zh' => {
    const langFromQuery = searchParams.get('lang');
    if (langFromQuery) return normalizeSiteLang(langFromQuery);

    return normalizeSiteLang(language);
};

const isValidContentId = (id: string | null): boolean => {
    if (!id || id.trim() === '') return false;
    return /^[a-zA-Z0-9_-]+$/.test(id);
};

const isValidPid = (pid: string | null): boolean => {
    if (!pid) return true; // pid is optional
    // Pack ID should be numeric or alphanumeric
    return /^[a-zA-Z0-9_-]+$/.test(pid);
};

const isValidSid = (sid: string | null): boolean => {
    if (!sid) return true; // sid is optional
    // Subscription ID should be alphanumeric
    return /^[a-zA-Z0-9_-]*$/.test(sid);
};

const isValidVef = (vef: string | null): boolean => {
    if (!vef) return true; // vef is optional
    // Should be single character Y or N
    return /^[YN]?$/.test(vef);
};

const validateQueryParams = (searchParams: URLSearchParams): { valid: boolean; errorType?: string } => {
    const libid = searchParams.get('libid');
    const channelid = searchParams.get('channelid');
    const pid = searchParams.get('pid');
    const sid = searchParams.get('sid');
    const vef = searchParams.get('vef');

    const contentId = libid || channelid;
    if (contentId && !isValidContentId(contentId)) {
        return { valid: false, errorType: 'invalid_libid' };
    }

    if (!isValidPid(pid)) {
        return { valid: false, errorType: 'invalid_pid' };
    }

    if (!isValidSid(sid)) {
        return { valid: false, errorType: 'invalid_sid' };
    }

    if (!isValidVef(vef)) {
        return { valid: false, errorType: 'invalid_vef' };
    }

    return { valid: true };
};

const Recommendation: React.FC<RecommendationProps> = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const hasContent = Boolean(searchParams.get('libid') || searchParams.get('channelid'));
    const runtimeLanguage = resolveRuntimeLanguage(searchParams);

    useEffect(() => {
        // Validate query parameters early
        const paramValidation = validateQueryParams(searchParams);
        if (!paramValidation.valid) {
            const errorParam = paramValidation.errorType ? `?error=${paramValidation.errorType}` : '';
            navigate(`/subscriber-enquiry/${errorParam}`, { replace: true });
            return;
        }

        const langSuffix = `?lang=${encodeURIComponent(runtimeLanguage)}`;

        // GLOBAL REDIRECTION LOGIC (Matches subscribe.php)
        if (hasContent) {
            const libid = searchParams.get('libid');
            const channelid = searchParams.get('channelid');
            const pid = searchParams.get('pid');
            const sid = searchParams.get('sid');
            const vef = searchParams.get('vef');
            const pname_en = searchParams.get('pname_en');
            const pname_zh = searchParams.get('pname_zh');
            const lang = searchParams.get('lang') || runtimeLanguage;

            const pname = (lang === 'zh' ? pname_zh : pname_en) || searchParams.get('pname');

            const sspParams = new URLSearchParams();
            if (lang) sspParams.set('lang', lang);
            if (libid) {
                sspParams.set('libid', libid);
            } else if (channelid) {
                sspParams.set('channelid', channelid);
            }
            if (pid) sspParams.set('pid', pid);
            if (sid) sspParams.set('sid', sid);
            if (vef) sspParams.set('vef', vef);
            if (pname) sspParams.set('pname', pname);

            window.location.href = `${SSP_URL}choose-plan?${sspParams.toString()}`;
            return;
        } else {
            navigate(`/get-started${langSuffix}`, { replace: true });
            return;
        }
    }, [hasContent, navigate, runtimeLanguage, searchParams]);

    // Always show loader since we are always redirecting
    return (
        <div id="loader">
            <div className="spinner-border"></div>
        </div>
    );
};

export default Recommendation;